import PieChart from "@components/Charts/PieChart";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import { ClientContext } from "@context/Client/ClientContext";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useClientModelVariables, useModelScenario } from "@services/api";
import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";

export interface MediaMixScenarioPieChartProps {
  scenarioId: string;
}

const MediaMixScenarioPieChart = ({
  scenarioId
}: MediaMixScenarioPieChartProps) => {
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { data: scenario, isFetching: isLoadingScenario } = useModelScenario(
    scenarioId ?? ""
  );

  const { data: modelVars, isFetching: isLoadingExtVars } =
    useClientModelVariables(currentClient.model.id);

  const isLoading = useMemo(
    () => isLoadingScenario || isLoadingExtVars,
    [isLoadingScenario, isLoadingExtVars]
  );

  const externalVarNames: string[] = useMemo(() => {
    return (modelVars || [])
      .filter((modelVariable) => modelVariable.type === "external")
      .map((modelVar) => modelVar.name);
  }, [modelVars]);

  const mediaMixData = useMemo(() => {
    const mediaInputs = (scenario?.inputs || []).filter(
      (input) => !externalVarNames.includes(input.name)
    );

    const mediaInputNames = mediaInputs.map((mediaInput) => mediaInput.name);
    const mediaNames = new Set(mediaInputNames);
    const mediaSpendTotal = mediaInputs.reduce(
      (acc, input) => acc + input.value,
      0
    );

    const mediaAggregated = Array.from(mediaNames).map((mediaName) => {
      const mediaInputTotal = mediaInputs
        .filter((input) => input.name === mediaName)
        .reduce((acc, input) => acc + input.value, 0);
      return {
        name: mediaName,
        value: mediaInputTotal
      };
    });

    return mediaAggregated.map((media) => {
      const mediaVar = (modelVars || []).find(
        (modelVar) => modelVar.name === media.name
      );
      const mediaVarColor = mediaVar?.color || "#6172F3";
      return {
        id: `${media.name}-${media.value}`,
        label: media.name,
        value: mediaSpendTotal > 0 ? (media.value / mediaSpendTotal) * 100 : 0,
        color: mediaVarColor
      };
    });
  }, [externalVarNames, scenario, modelVars]);

  return (
    <Box className="w-full">
      {isLoading ? (
        <TitledChartSkeleton hasLegend />
      ) : (
        <Box className="w-full">
          <Typography variant="h6" component="div" className="pt-4 pl-6">
            {t("simulator.benchmark.media_mix_per_scenario", {
              scName: scenario?.scenario.name || "Scenario",
              currency: currentClient.client.currency
            })}
          </Typography>
          <Box sx={{ height: "500px", width: "100%" }}>
            {scenario && (
              <PieChart
                key={`media-mix-scenario-pie-chart-${scenarioId}`}
                data={mediaMixData}
                legendProps={{
                  onMouseEnter: undefined,
                  onMouseLeave: undefined,
                  effects: undefined,
                  anchor: isMobile ? "bottom" : "right",
                  direction: "column",
                  translateY: isMobile ? 150 : 0,
                  translateX: isMobile ? -10 : 40
                }}
                responsivePieProps={{
                  margin: { top: 20, bottom: 20, left: -60, right: 150 }
                }}
              />
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MediaMixScenarioPieChart;
